import axios from 'axios';
import { environment } from '../environments/environment';

// Use centralized environment configuration
const API_BASE_URL = environment.apiUrl;

// Create axios instance with auth token
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Team Session Service
 * Manages explicit team sessions for concurrent user tracking
 *
 * IMPLICIT SESSION MANAGEMENT:
 * - Sessions are now automatically managed by the backend
 * - Every team operation (search, view content, etc.) automatically validates/updates the session
 * - No explicit heartbeat needed - session activity is tracked implicitly
 * - Sessions auto-expire after 30 minutes of inactivity
 * - Stale sessions are cleaned up lazily during capacity checks
 *
 * Client Responsibilities (simplified):
 * - Call startSession when explicitly reserving sessions (e.g., cross-team search)
 * - Call endSession when explicitly releasing sessions
 * - For normal team operations, do nothing - backend handles everything!
 */
class TeamSessionService {
  private activeSessions: Set<string> = new Set();

  /**
   * Start a team session (explicit session reservation)
   *
   * NOTE: This is only needed for explicit multi-session scenarios like cross-team search.
   * For normal team operations, sessions are managed automatically by the backend.
   *
   * @param teamId - Team ID to activate
   * @returns Promise with success status and subscription info
   */
  async startSession(teamId: string): Promise<{
    success: boolean;
    errorCode?: 'SUBSCRIPTION_EXPIRED' | 'LIMIT_EXCEEDED';
    error?: string;
    subscriptionInfo?: {
      currentConcurrentUsers: number;
      maxConcurrentUsers: number;
      utilizationPercent: number;
      atCapacity: boolean;
    };
  }> {
    try {
      console.log(`[TeamSessionService] Explicitly starting session for team: ${teamId}`);

      const response = await api.post(`/teams/${teamId}/session/start`);

      // Success responses have status 200 and include 'data' field
      // Check if response has data (success case)
      if (response.data.data) {
        // Track that we explicitly started this session
        this.activeSessions.add(teamId);

        console.log(`[TeamSessionService] Session started successfully for team: ${teamId}`, {
          subscriptionInfo: response.data.data.subscriptionInfo
        });

        return {
          success: true,
          subscriptionInfo: response.data.data.subscriptionInfo
        };
      }

      // If no data field, treat as error
      return {
        success: false,
        error: response.data.message || 'Failed to start session'
      };
    } catch (error: any) {
      console.error(`[TeamSessionService] Failed to start session for team: ${teamId}`, error);

      // Subscription expired — inform the caller to show the renewal prompt
      if (error.response?.status === 402) {
        const errorData = error.response.data.error;
        return {
          success: false,
          errorCode: 'SUBSCRIPTION_EXPIRED',
          error: errorData?.message || 'Your subscription has expired. Please renew to continue.'
        };
      }

      // Concurrent user limit reached
      if (error.response?.status === 429) {
        const errorData = error.response.data.error;
        return {
          success: false,
          errorCode: 'LIMIT_EXCEEDED',
          error: errorData?.message || 'Team has reached maximum concurrent users',
          subscriptionInfo: errorData?.details
        };
      }

      return {
        success: false,
        error: error.response?.data?.error?.message || 'Failed to start session'
      };
    }
  }

  /**
   * End a specific team session (explicit session release)
   *
   * NOTE: Only needed if you explicitly called startSession().
   * Sessions created implicitly by team operations will auto-expire after 30 minutes of inactivity.
   *
   * @param teamId - Team ID to deactivate
   */
  async endSession(teamId: string): Promise<void> {
    try {
      console.log(`[TeamSessionService] Ending session for team: ${teamId}`);

      // Remove from tracked sessions
      this.activeSessions.delete(teamId);

      // Call API to end session
      await api.delete(`/teams/${teamId}/session/end`);

      console.log(`[TeamSessionService] Session ended for team: ${teamId}`);
    } catch (error: any) {
      console.error(`[TeamSessionService] Failed to end session for team: ${teamId}`, error);
      // Don't throw - ending session should be best-effort
    }
  }

  /**
   * End all team sessions (logout scenario)
   */
  async endAllSessions(): Promise<void> {
    try {
      console.log('[TeamSessionService] Ending all sessions');

      // Clear tracked sessions
      this.activeSessions.clear();

      // Call API to end all sessions (no /api/v2 prefix - axios baseURL already includes it)
      await api.delete('/session/end-all');

      console.log('[TeamSessionService] All sessions ended');
    } catch (error: any) {
      console.error('[TeamSessionService] Failed to end all sessions', error);
      // Don't throw - cleanup should be best-effort
    }
  }

  /**
   * Get all active sessions for current user
   */
  async getActiveSessions(): Promise<Array<{
    teamId: string;
    subscriptionId: string;
    activatedAt: string;
    lastActivity: string;
  }>> {
    try {
      const response = await api.get('/api/v2/users/me/sessions');

      // Success responses have 'data' field per API Response Standard
      if (response.data.data) {
        return response.data.data.sessions || [];
      }

      return [];
    } catch (error: any) {
      console.error('[TeamSessionService] Failed to get active sessions', error);
      return [];
    }
  }

  /**
   * Check if we explicitly started a session for this team
   * (Does not reflect implicit sessions created by backend)
   */
  hasExplicitSession(teamId: string): boolean {
    return this.activeSessions.has(teamId);
  }

  /**
   * Get count of explicitly started sessions
   * (Does not reflect implicit sessions created by backend)
   */
  getExplicitSessionCount(): number {
    return this.activeSessions.size;
  }

  /**
   * Cleanup on service destruction (component unmount, page unload, etc.)
   */
  destroy(): void {
    console.log('[TeamSessionService] Destroying service');
    this.activeSessions.clear();
  }
}

// Export singleton instance
export const teamSessionService = new TeamSessionService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // Note: We don't call endAllSessions here because it's async
    // The browser will close before the request completes
    // The backend's lazy cleanup will handle stale sessions automatically
    teamSessionService.destroy();
  });
}

export default teamSessionService;
